/**
 * 流水列表的**按日分组**（纯逻辑 · C 域 PRD **§5.1.2**「按日分组，带日期分隔头」· `AC-C1.5`）
 *
 * ## 🔴 为什么分组在端侧做（不是服务端）
 * 服务端 `TransactionQueryService.list()` 的文件头已明文交代：
 * 「分组（按日）不在服务端做 —— §5.1.2「单日笔数跨批次时**重复出现分组头**」这条要求本身就说明
 * 分组是**渲染期**的事：服务端已按 `date DESC` 排好，端侧顺序扫一遍遇到新日期就插一个分隔头即可。」
 * ⇒ 服务端只保证**顺序**（`date DESC, createdAt DESC, id`），分组归本文件。
 *
 * ## 🔴 「跨批次重复分组头」是**要求**，不是缺陷 —— 所以按"批"分组再拼接
 * 若把累计到的全部行**当作一整段**再分组，同一个日期只会有一个头
 * （第二页若从某天的中间开始，那天就不会再出现头）。而 §5.1.2 / `AC-C1.5` 要的正是
 * **每批各自带头**（v1 的加载形态）⇒ 本函数签名收的是 **`pages`（按批）**，逐批分组后拼接，
 * 同一天在批边界处**允许出现两个头**。把它写成"合并后分组"会让这条 AC **静默不成立**。
 *
 * ⚠️ 也因此**不要**把这里改成"去重合并" —— 那是改产品行为，不是优化。
 */

import { addDays, parseDay } from '@jiaying/shared';

/** 一个日期分组（`items` 至少一条） */
export interface JournalSection<T> {
  date: string;
  /** 日期头的展示文案（`今天` / `昨天` / `9月18日 周四`） */
  label: string;
  items: readonly T[];
}

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const;

/**
 * 日期头文案。
 *
 * 🔴 用 `Date.UTC` + `getUTCDay()` 取星期：**不用本地时区**（`new Date('2026-09-18')` 会被解析成
 * UTC 午夜，再用 `getDay()` 会在负时区差一天 —— 而那正是"看起来正常的错日期"）。
 */
export function dayLabel(day: string, today: string): string {
  if (day === today) return '今天';
  if (day === addDays(today, -1)) return '昨天';
  const { y, m, d } = parseDay(day);
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const md = `${String(m)}月${String(d)}日`;
  return y === parseDay(today).y ? `${md} ${weekday}` : `${String(y)}年${md}`;
}

/**
 * 逐批分组后拼接（见文件头「跨批次重复分组头」）。
 *
 * @param pages 按加载顺序的若干页（`pages[0]` 是最新的一页）
 * @param today 账本时区下的今天 —— 只用于 `今天` / `昨天` 两个文案
 */
export function sectionizePages<T extends { readonly date: string }>(
  pages: readonly (readonly T[])[],
  today: string,
): JournalSection<T>[] {
  const out: JournalSection<T>[] = [];
  for (const page of pages) {
    let current: { date: string; items: T[] } | null = null;
    for (const item of page) {
      if (current === null || current.date !== item.date) {
        current = { date: item.date, items: [] };
        out.push({ date: current.date, label: dayLabel(current.date, today), items: current.items });
      }
      current.items.push(item);
    }
  }
  return out;
}

/** 累计已加载的笔数（"共 N 笔"里的 N 用 `Paged.total`，这里只用于显示"已加载 N 笔"） */
export function loadedCount<T>(pages: readonly (readonly T[])[]): number {
  return pages.reduce((n, p) => n + p.length, 0);
}
